// src/utils/galleryLoader.js

/**
 * Lädt Kunstwerke aus der JSON-Datei
 * @returns {Promise<Array>} Array von Kunstwerken
 */
export const loadArtworks = async () => {
  try {
    const response = await fetch(`${import.meta.env.BASE_URL}data/artworks.json`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  } catch (error) {
    console.error('❌ Fehler beim Laden der Galerie:', error);
    return [];
  }
};

/**
 * Filtert Kunstwerke nach Kategorie
 * @param {Array} artworks - Alle Kunstwerke
 * @param {string} category - Kategorie zum Filtern
 * @returns {Array} Gefilterte Kunstwerke
 */
export const filterByCategory = (artworks, category) => {
  if (!category || category === 'all') return artworks;
  return artworks.filter(art => art.category === category);
};

/**
 * Sucht Kunstwerke nach Titel oder Künstler
 * @param {Array} artworks - Alle Kunstwerke
 * @param {string} query - Suchbegriff
 * @returns {Array} Gefundene Kunstwerke
 */
export const searchArtworks = (artworks, query) => {
  if (!query || query.trim() === '') return artworks;
  
  const lowerQuery = query.toLowerCase();
  
  return artworks.filter(art => 
    art.title?.toLowerCase().includes(lowerQuery) ||
    art.artist?.toLowerCase().includes(lowerQuery) ||
    art.category?.toLowerCase().includes(lowerQuery)
  );
};

/**
 * Sortiert Kunstwerke
 * @param {Array} artworks - Zu sortierende Kunstwerke
 * @param {string} sortBy - 'title' | 'artist' | 'date' | 'random'
 * @returns {Array} Sortierte Kunstwerke
 */
export const sortArtworks = (artworks, sortBy = 'title') => {
  const sorted = [...artworks];
  
  switch (sortBy) {
    case 'title':
      return sorted.sort((a, b) => a.title?.localeCompare(b.title));
      
    case 'artist':
      return sorted.sort((a, b) => a.artist?.localeCompare(b.artist));
      
    case 'date':
      return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      
    case 'random':
      return sorted.sort(() => Math.random() - 0.5);
      
    default:
      return sorted;
  }
};

/**
 * Gruppiert Kunstwerke nach Kategorie
 * @param {Array} artworks - Alle Kunstwerke
 * @returns {Object} Gruppierte Kunstwerke { kategorie: [werke] }
 */
export const groupByCategory = (artworks) => {
  return artworks.reduce((groups, art) => {
    const cat = art.category || 'Uncategorized';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(art);
    return groups;
  }, {});
};

/**
 * Validiert ein Kunstwerk-Objekt
 * @param {Object} artwork - Zu prüfendes Objekt
 * @returns {boolean} Ist gültig?
 */
export const validateArtwork = (artwork) => {
  return (
    artwork &&
    typeof artwork === 'object' &&
    artwork.file &&
    artwork.title &&
    artwork.artist
  );
};

/**
 * Bereitet Kunstwerke für die Anzeige vor (validiert, ergänzt Defaults)
 * @param {Array} artworks - Rohe Kunstwerke
 * @returns {Array} Aufbereitete Kunstwerke
 */
export const prepareArtworks = (artworks) => {
  return artworks
    .filter(validateArtwork)
    .map((art, index) => ({
      ...art,
      id: art.id || `art-${index}`,
      watermark: art.watermark || '© GAIAMUNDI',
      category: art.category || 'General',
      date: art.date || new Date().toISOString(),
    }));
};

// Default Export für einfacheren Import
export default {
  loadArtworks,
  filterByCategory,
  searchArtworks,
  sortArtworks,
  groupByCategory,
  validateArtwork,
  prepareArtworks,
};